import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Inbox, Check } from "lucide-react";

export default function AdminVideoGuides({ lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [guides, setGuides] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", category: "Primi passi", order: 0 });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, r] = await Promise.all([api.get("/video-guides"), api.get("/admin/video-guides/requests")]);
      setGuides(g.data.guides); setRequests(r.data.requests);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!form.title.trim() || !file) { toast.error(L("Titolo e file obbligatori", "Title and file required")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title); fd.append("description", form.description);
      fd.append("category", form.category); fd.append("order", String(form.order || 0));
      fd.append("file", file);
      await api.post("/admin/video-guides", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(L("Video caricato", "Video uploaded"));
      setForm({ title: "", description: "", category: "Primi passi", order: 0 }); setFile(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const del = async (id) => { if (!window.confirm(L("Eliminare questa guida?", "Delete this guide?"))) return;
    try { await api.delete(`/admin/video-guides/${id}`); toast.success(L("Eliminata", "Deleted")); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const resolve = async (id) => { try { await api.patch(`/admin/video-guides/requests/${id}`, { status: "risolta" }); load(); } catch (e) { toast.error("Error"); } };

  if (loading) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-guides">
      {/* Upload + list */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-card border border-border rounded-lg p-4 space-y-2" data-testid="guide-upload-form">
          <h3 className="font-semibold">{L("Carica una video guida", "Upload a video guide")}</h3>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="guide-title"
            placeholder={L("Titolo (es. Cosa fare appena accedi)", "Title (e.g. What to do first)")}
            className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="guide-desc"
            placeholder={L("Descrizione", "Description")} rows={2} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="guide-category"
              placeholder={L("Categoria", "Category")} className="flex-1 h-9 rounded-md border border-border bg-background px-3 text-sm" />
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="guide-order"
              placeholder="Ordine" className="w-24 h-9 rounded-md border border-border bg-background px-3 text-sm" />
          </div>
          <input type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="guide-file"
            className="w-full text-sm" />
          <button onClick={upload} disabled={uploading} data-testid="guide-upload-btn"
            className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{L("Carica", "Upload")}
          </button>
        </div>

        <div className="border border-border rounded-lg overflow-hidden">
          {guides.length === 0 ? <p className="text-sm text-muted-foreground p-4">{L("Nessuna guida caricata.", "No guides uploaded.")}</p> : (
            <table className="w-full text-sm"><tbody>
              {guides.map((g) => (
                <tr key={g.id} className="border-b border-border last:border-0" data-testid={`admin-guide-${g.id}`}>
                  <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-muted mr-2">{g.category}</span><b>{g.title}</b></td>
                  <td className="px-3 py-2.5 text-right"><button onClick={() => del(g.id)} data-testid={`guide-delete-${g.id}`} className="p-1.5 hover:bg-muted rounded text-red-500"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody></table>
          )}
        </div>
      </div>

      {/* Requests */}
      <div className="bg-card border border-border rounded-lg p-4" data-testid="guide-requests">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Inbox className="w-4 h-4" />{L("Richieste di spiegazione", "Explanation requests")}</h3>
        {requests.length === 0 ? <p className="text-sm text-muted-foreground">{L("Nessuna richiesta.", "No requests.")}</p> : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className={`rounded-lg border p-3 ${r.status === "risolta" ? "border-border opacity-60" : "border-amber-500/40 bg-amber-500/5"}`} data-testid={`guide-req-${r.id}`}>
                <p className="text-sm font-medium">{r.topic}</p>
                {r.note && <p className="text-xs text-muted-foreground mt-1">{r.note}</p>}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">{r.user_email}</span>
                  {r.status !== "risolta" && <button onClick={() => resolve(r.id)} data-testid={`guide-req-resolve-${r.id}`} className="text-xs flex items-center gap-1 text-emerald-600 hover:underline"><Check className="w-3.5 h-3.5" />{L("Segna risolta", "Mark resolved")}</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
